import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { extractApiError, neurohamApi, NEUROHAM_TONE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Flame, X, Send, Loader2, Trash2, ArrowRight } from "lucide-react";

// Нейрохам — развлекательный бот. Цифр не называет принципиально: подкалывает
// и кидает ссылку на нужную вкладку. Живёт плавающей кнопкой поверх всех
// страниц, чтобы отвлечься можно было не бросая работу, и передаёт бэкенду
// текущий маршрут — тогда шутка «ты стоишь ровно на той вкладке» становится
// возможной.

const GREETING = {
    role: "bot",
    id: "__greeting",
    text: "Ну чего тебе? Учти: цифры я не показываю принципиально — за это тут отдельные вкладки. Могу подсказать, куда идти.",
    route: "",
    route_label: "",
};

function Bubble({ message, onGo }) {
    const mine = message.role === "user";
    return (
        <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[85%]">
                <div
                    className="rounded-2xl px-3 py-2 text-sm"
                    style={{
                        background: mine ? "var(--accent-light)" : "var(--glass-bg)",
                        border: `1px solid ${mine ? "var(--accent-border)" : "var(--glass-border)"}`,
                        color: "var(--foreground)",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                    }}
                >
                    {message.text}
                </div>
                {message.route && (
                    <button
                        type="button"
                        onClick={() => onGo(message.route)}
                        className="mt-1.5 inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg"
                        style={{ background: "var(--accent-light)", border: "1px solid var(--accent-border)", color: "var(--accent)" }}
                    >
                        {message.route_label || "Открыть"} <ArrowRight className="w-3 h-3" />
                    </button>
                )}
            </div>
        </div>
    );
}

export default function NeurohamWidget() {
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [messages, setMessages] = useState([]);
    const [tone, setTone] = useState("cheeky");
    const [usage, setUsage] = useState({ used_today: 0, daily_limit: 0 });
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const endRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    // Историю тянем только когда окно реально открыли — незачем ходить на
    // бэкенд на каждой загрузке страницы ради развлекательной функции.
    useEffect(() => {
        if (!open || loaded) return;
        neurohamApi.history()
            .then(({ data }) => {
                setMessages(data.messages || []);
                setTone(data.tone || "cheeky");
                setUsage({ used_today: data.used_today || 0, daily_limit: data.daily_limit || 0 });
            })
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, [open, loaded]);

    useEffect(() => {
        if (open) endRef.current?.scrollIntoView({ block: "nearest" });
    }, [messages, open]);

    const send = async () => {
        const value = text.trim();
        if (!value || sending) return;
        setSending(true);
        setText("");
        try {
            const { data } = await neurohamApi.send(value, location.pathname);
            setMessages((prev) => [...prev, data.message, data.reply]);
            setUsage({ used_today: data.used_today, daily_limit: data.daily_limit });
        } catch (e) {
            toast.error(extractApiError(e, "Нейрохам сегодня не в духе"));
            setText(value);
        } finally {
            setSending(false);
        }
    };

    const clear = async () => {
        try {
            await neurohamApi.clear();
            setMessages([]);
        } catch (e) {
            toast.error(extractApiError(e, "Не удалось очистить переписку"));
        }
    };

    const shown = messages.length ? messages : [GREETING];
    const left = Math.max(0, (usage.daily_limit || 0) - (usage.used_today || 0));

    return (
        <>
            {open && (
                <div
                    className="fixed z-50 flex flex-col rounded-2xl overflow-hidden"
                    style={{
                        right: 16, bottom: 84,
                        width: "min(380px, calc(100vw - 32px))",
                        height: "min(520px, calc(100vh - 140px))",
                        background: "var(--card-bg)",
                        border: "1px solid var(--card-border)",
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        boxShadow: "var(--glass-shadow-lg)",
                    }}
                    data-testid="neuroham-panel"
                >
                    <div className="flex items-center justify-between gap-2 px-3 py-2.5" style={{ borderBottom: "1px solid var(--card-border)" }}>
                        <div className="flex items-center gap-2 min-w-0">
                            <Flame className="w-4 h-4 shrink-0" style={{ color: "#f97316" }} />
                            <span className="font-semibold text-sm" style={{ color: "var(--foreground)" }}>Нейрохам</span>
                            <span className="text-[11px] truncate" style={{ color: "var(--foreground-3)" }}>
                                {NEUROHAM_TONE_LABELS[tone] || ""}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button type="button" onClick={clear} style={{ color: "var(--foreground-3)" }} aria-label="Очистить переписку" title="Очистить переписку">
                                <Trash2 className="w-4 h-4" />
                            </button>
                            <button type="button" onClick={() => setOpen(false)} style={{ color: "var(--foreground-3)" }} aria-label="Закрыть">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        {shown.map((m) => (
                            <Bubble key={m.id} message={m} onGo={(route) => { navigate(route); setOpen(false); }} />
                        ))}
                        {sending && (
                            <div className="flex items-center gap-2 text-xs" style={{ color: "var(--foreground-3)" }}>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> печатает что-то дерзкое…
                            </div>
                        )}
                        <div ref={endRef} />
                    </div>

                    <div className="p-2.5 space-y-1.5" style={{ borderTop: "1px solid var(--card-border)" }}>
                        <div className="flex items-end gap-2">
                            <Textarea
                                rows={2}
                                value={text}
                                disabled={sending}
                                placeholder="Спроси что-нибудь…"
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        send();
                                    }
                                }}
                            />
                            <Button size="sm" onClick={send} disabled={sending || !text.trim()} aria-label="Отправить">
                                <Send className="w-4 h-4" />
                            </Button>
                        </div>
                        <div className="flex items-center justify-between text-[10px]" style={{ color: "var(--foreground-3)" }}>
                            <span>Enter — отправить</span>
                            {usage.daily_limit > 0 && <span>осталось сегодня: {left}</span>}
                        </div>
                    </div>
                </div>
            )}

            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="fixed z-50 flex items-center justify-center rounded-full transition-transform hover:scale-105"
                style={{
                    right: 16, bottom: 16, width: 52, height: 52,
                    background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                    boxShadow: "0 6px 24px rgba(249,115,22,0.45)",
                    color: "#fff",
                }}
                aria-label="Нейрохам"
                title="Нейрохам"
                data-testid="neuroham-button"
            >
                {open ? <X className="w-5 h-5" /> : <Flame className="w-6 h-6" />}
            </button>
        </>
    );
}
